import "./GLogos.scss";
import {
    ArrowRight,
    Code2,
    Laptop,
    Lightbulb,
    Rocket,
    Sparkles,
    Zap,
} from "lucide-react";
import {useTranslation} from "react-i18next";
import {DiscordMember, GalleryItem} from "../../types";
import {useNavigate} from "react-router-dom";
import Card from "../../Components/CardGallery/CardGallery";
import {discordUrl} from "../../Global";
import {useState} from "react";


const steps = [
    {
        id: "01",
        icon: Lightbulb,
    },
    {
        id: "02",
        icon: Laptop,
    },
    {
        id: "03",
        icon: Code2,
    },
    {
        id: "04",
        icon: Rocket,
    },
];

interface GLogosProps {
    staff: DiscordMember[] | [];
    workGraphic: GalleryItem[];
}



export default function GLogos({ staff, workGraphic }: GLogosProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const ourLogos = workGraphic.filter(item => item.type === "logo");

    const graphicsLogos = staff.filter((member) =>
        member.roles.some((role) => role.id === '1558567341347053588')
    );

    const [maxPhoto, setMaxPhoto] = useState<number>(5);

    return (
        <section className="graphics-logos">
            {/* ===== HERO ===== */}

            <div className="graphics-logos__hero">
                <div className="graphics-logos__hero-container">
                    {/* Left */}
                    <div className="graphics-logos__hero-content">
                        <h1 className="graphics-logos__title">
                            {t("logo-design.title.up")}
                            <br />
                            {t("logo-design.title.down")}<span>{t("logo-design.title.bold")}</span>
                        </h1>
                        <p className="graphics-logos__description">
                            {t("logo-design.subtitle")}
                        </p>
                        <div className="graphics-logos__actions">
                            <a href={discordUrl} target="_blank" className="graphics-logos__button" rel="noreferrer">
                                {t("logo-design.preventive")}
                                <ArrowRight size={18} />
                            </a>
                            {workGraphic.length > 0 && (
                                <a href="#portfolio" className="graphics-logos__button graphics-logos__button--secondary">
                                    {t("logo-design.lookWorks")}
                                </a>
                            )}
                        </div>
                    </div>

                    {/*Presentation*/}
                </div>
            </div>

            {/* ===== STEP ===== */}
            <div className="graphics-logos__steps">
                <div className="graphics-logos__steps-container">
                    <header className="graphics-logos__steps-header">
                        <h2>{t(`logo-design.develop.title`)}</h2>
                        <p>{t(`logo-design.develop.subtitle`)}</p>
                    </header>

                    <div className="graphics-logos__steps-grid">
                        {steps.map((step, index) => {
                            const Icon = step.icon;
                            return (
                                <div className="graphics-logos__step" key={step.id}>
                                    <div className="graphics-logos__step-icon">
                                        <Icon size={24} />
                                    </div>
                                    <h3 className="graphics-logos__step-title">{t(`logo-design.develop.card.${step.id}.title`)}</h3>
                                    <p className="graphics-logos__step-description">{t(`logo-design.develop.card.${step.id}.subtitle`)}</p>
                                    {index < steps.length - 1 && (
                                        <div className="graphics-logos__step-connector">
                                            <Zap size={14} />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* ===== TEAM ===== */}
            {graphicsLogos.length > 0 && (
                <div className="graphics-logos__team">
                    <div className="graphics-logos__team-container">
                        <header className="graphics-logos__team-header">
                            <h2>{t(`logo-design.employees.title`)}</h2>
                            <p>{t(`logo-design.employees.subtitle`)}</p>
                        </header>

                        <div className="graphics-logos__team-grid">
                            {graphicsLogos.map((dev) => (
                                <a
                                    key={dev.id}
                                    target="_blank"
                                    href={dev.avatar}
                                    rel="noopener noreferrer"
                                    className="graphics-logos__team-card"
                                >
                                    <div className="graphics-logos__team-avatar">
                                        <img src={dev.avatar} alt={dev.username} />
                                        <div className={`graphics-logos__team-status graphics-logos__team-status_${dev.online}`} />
                                    </div>
                                    <h3>{dev.username}</h3>
                                    <span>Logo Designer</span>
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ===== PORTFOLIO ===== */}
            {ourLogos.length > 0 && (
            <div className="graphics-logos__portfolio" id="portfolio">
                <div className="graphics-logos__portfolio-container">
                    <header className="graphics-logos__portfolio-header">
                        <h2>{t(`logo-design.works.title`)}</h2>
                        <p>{t(`logo-design.works.subtitle`)}</p>
                    </header>

                    <div className="graphics-drawings__gallery__container">
                        <div className="gallery">
                            {ourLogos.slice(0, maxPhoto).map((photos) => (
                                <Card
                                    item={photos}
                                />
                            ))}
                            {ourLogos.length > maxPhoto && (
                            <div onClick={() => setMaxPhoto(ourLogos.length)} className="card gallery-more-card">
                                <div className="gallery-more-card__content">
                                    <p>{t("works.graphic.other")}</p>
                                    <h1>+{ourLogos.length - maxPhoto}</h1>
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            )}
        </section>
    );
}

